import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from './AuthLayout';
import { SocialLoginButtons } from './components/SocialLoginButtons';
import { loginSchema, LoginFormData } from './utils/validation';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { authApi } from '../../services/auth.api';
import { useAuthStore } from '../../store/authStore';
import { ApiError } from '../../services/api';
import { useBranding } from '../../utils/branding';

export function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const branding = useBranding();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || undefined;
  const [isLoading, setIsLoading] = useState(false);
  const [inputError, setInputError] = useState<string | undefined>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const setAuth = useAuthStore((s) => s.setAuth);

  const { watch, setValue, handleSubmit } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      contact: '',
      countryCode: '+1',
    },
  });

  const contact = watch('contact');

  const onSubmit = async (data: LoginFormData) => {
    const email = data.contact.trim().toLowerCase();

    // Basic email validation
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setInputError(t('errors.invalidEmail'));
      return;
    }

    setInputError(undefined);
    setIsLoading(true);

    try {
      const loginResult = await authApi.login({ email });

      navigate('/auth/verify-otp', {
        state: {
          emailOrPhone: email,
          inputType: 'email',
          otpType: loginResult.requiresEmailVerification ? 'EMAIL_VERIFY' : 'LOGIN_OTP',
          redirectTo,
        },
      });
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 403 || error.message?.toLowerCase().includes('not verified')) {
          navigate('/auth/verify-otp', {
            state: {
              emailOrPhone: email,
              inputType: 'email',
              otpType: 'EMAIL_VERIFY',
              redirectTo,
            },
          });
          return;
        }
        setInputError(error.message);
      } else {
        setInputError(t('errors.genericError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Redirect to backend OAuth flow
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const redirectTo = searchParams.get('redirect') || '/dashboard';
    window.location.href = `${apiUrl}/v1/auth/google/login?redirectTo=${encodeURIComponent(redirectTo)}`;
  };

  const handleContactChange = (value: string) => {
    setValue('contact', value);
    if (inputError) setInputError(undefined);
  };

  return (
    <AuthLayout>
      <div className="space-y-7">
        {/* Header */}
        <div className="animate-fade-in-up stagger-1">
          <h2 className="text-2xl font-normal text-slate-900 dark:text-zinc-100 tracking-tight">{t('login.title')}</h2>
          <p className="mt-1.5 text-sm text-slate-500 dark:text-zinc-400">{t('login.subtitle')}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" data-testid="login-form">
          {/* Error Banner */}
          {inputError && !contact && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50/80 border border-red-200 animate-fade-in">
              <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900">{inputError}</p>
              </div>
              <button
                type="button"
                onClick={() => setInputError(undefined)}
                className="text-red-400 hover:text-red-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
          
          {/* Email Input */}
          <div className="space-y-1 animate-fade-in-up stagger-2">
            <label className="text-xs font-normal uppercase tracking-wide text-slate-500 dark:text-zinc-400">
              {t('login.emailLabel')}
            </label>
            <input
              type="email"
              value={contact}
              onChange={(e) => handleContactChange(e.target.value)}
              placeholder={t('login.emailPlaceholder')}
              disabled={isLoading}
              autoFocus
              className={cn(
                'flex h-12 w-full rounded-xl border bg-white dark:bg-zinc-900 px-4 py-2 text-sm transition-all',
                'placeholder:text-slate-400 dark:placeholder:text-zinc-500 text-slate-900 dark:text-zinc-100',
                'focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-zinc-100/10 focus:border-slate-900 dark:focus:border-zinc-400',
                'disabled:cursor-not-allowed disabled:opacity-50',
                contact && inputError
                  ? 'border-red-500 ring-2 ring-red-500/10 hover:border-red-500'
                  : 'border-slate-200 dark:border-zinc-700 hover:border-slate-300 dark:hover:border-zinc-500'
              )}
            />
            {contact && inputError && (
              <p className="text-xs text-red-500 px-1">{inputError}</p>
            )}
          </div>

          {/* Submit */}
          <div className="animate-fade-in-up stagger-3">
          <Button
            type="submit"
            data-testid="login-button"
            className="w-full h-12 text-sm font-normal bg-slate-900 hover:bg-slate-800 text-white rounded-xl transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.98]"
            disabled={isLoading || !contact.trim()}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                {t('login.signingIn')}
              </span>
            ) : (
              t('login.continue')
            )}
          </Button>
          </div>

          {/* Divider */}
          <div className="relative animate-fade-in stagger-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-100" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white dark:bg-zinc-950 px-4 text-xs uppercase tracking-widest text-slate-400 dark:text-zinc-500 font-medium">
                {t('login.orContinueWith')}
              </span>
            </div>
          </div>

          {/* Social Buttons */}
          <div className="animate-fade-in-up stagger-5">
            <SocialLoginButtons
              onGoogleLogin={handleGoogleLogin}
              onAppleLogin={() => {}}
              onMetaLogin={() => {}}
              disabled={isLoading}
            />
          </div>

          {/* Footer link */}
          <p className="text-center text-sm text-slate-500 dark:text-zinc-400 pt-1 animate-fade-in stagger-6">
            {t('login.noAccount')}{' '}
            <button
              type="button"
              onClick={() => navigate('/auth/register')}
              disabled={isLoading}
              className="font-normal text-slate-900 dark:text-zinc-100 hover:underline underline-offset-2"
            >
              {t('login.createAccount')}
            </button>
          </p>
        </form>
      </div>
    </AuthLayout>
  );
}

